import React, { useState, useCallback } from "react";
import { motion } from "framer-motion";
import { authApi, captchaFromError } from "../api/client";
import { COLORS } from "../theme/colors";
import { MATERIALS, RADIUS } from "../theme/materials";
import { useTheme } from "../contexts/ThemeContext";
import { Input } from "./ui";
import AmbientOrbs from "./AmbientOrbs";
import Logo from "./Logo";
import TurnstileWidget from "./TurnstileWidget";

// I² Visual Identity v1 — R2.1 Login pilot (2026-04-29).
// Per design doc §6 Ring 2, sign-off Q1 batched commit (R2.1+R2.2).
// Login is the brand-defining first surface: gradient backdrop, ambient
// orbs, thinGlass card with grain overlay, spring entry via framer-motion.

export default function LoginPage({ onLogin, inviteToken, resetToken }) {
  const { mode: themeMode, toggleTheme } = useTheme();
  const [mode, setMode] = useState("login"); // login | register | forgot
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(() => {
    if (sessionStorage.getItem("session_expired")) {
      sessionStorage.removeItem("session_expired");
      return "Your session has expired. Please sign in again.";
    }
    return null;
  });
  const [success, setSuccess] = useState(null);

  // Login fields
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // A1-CAPTCHA: Turnstile challenge shown after N failed logins.
  const [captcha, setCaptcha] = useState({ required: false, sitekey: "" });
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaError, setCaptchaError] = useState(false);
  const onCaptchaError = useCallback(() => setCaptchaError(true), []);
  const [tenantSlug, setTenantSlug] = useState("");

  // Invite accept fields
  const [invitePassword, setInvitePassword] = useState("");
  const [inviteConfirm, setInviteConfirm] = useState("");

  // Forgot password fields
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSlug, setForgotSlug] = useState("");

  // Reset password fields
  const [resetPassword, setResetPassword] = useState("");
  const [resetConfirm, setResetConfirm] = useState("");
  const [resetDone, setResetDone] = useState(false);

  // Register fields
  const [regName, setRegName] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [regTenantName, setRegTenantName] = useState("");
  const [regTenantSlug, setRegTenantSlug] = useState("");

  const handleAcceptInvite = async (e) => {
    e.preventDefault();
    if (invitePassword !== inviteConfirm) {
      setError("Passwords do not match");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const res = await authApi.acceptInvite(inviteToken, invitePassword);
      if (res.success) {
        window.history.replaceState({}, "", window.location.pathname);
        onLogin(res.data);
      }
    } catch (err) {
      setError(err.message || "Failed to accept invitation");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);
    try {
      await authApi.forgotPassword(forgotEmail, forgotSlug);
      setSuccess("If an account exists with that email, a reset link has been sent. Check your email or backend console.");
    } catch (err) {
      setError(err.message || "Failed to send reset link");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (resetPassword !== resetConfirm) {
      setError("Passwords do not match");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      await authApi.resetPassword(resetToken, resetPassword);
      setResetDone(true);
      window.history.replaceState({}, "", window.location.pathname);
    } catch (err) {
      setError(err.message || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await authApi.login(email, password, tenantSlug, captchaToken);
      if (res.success) onLogin(res.data);
    } catch (err) {
      // A1-CAPTCHA: after N fails the backend asks for a Turnstile challenge.
      const cap = captchaFromError(err);
      if (cap.required) {
        setCaptcha(cap);
        setCaptchaToken("");    // require a fresh solve
        setCaptchaError(false); // give the widget a clean chance to load
      }
      // Meaningful, actionable messages:
      if (err.status === 429) {
        setError("Too many sign-in attempts. Please wait a few minutes and try again — or reset your password using the link below.");
      } else if (cap.required) {
        setError("One more step — complete the quick verification below, then sign in again.");
      } else {
        setError(err.message || "We couldn't sign you in. Double-check your workspace ID, email, and password.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await authApi.register(regTenantName, regTenantSlug, regName, regEmail, regPassword);
      if (res.success) onLogin(res.data);
    } catch (err) {
      setError(err.message || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const loginInputStyle = {
    padding: "12px 14px", borderRadius: RADIUS.inner, fontSize: 14, fontFamily: "'DM Sans', sans-serif",
  };

  // Moderation pass 2026-04-29: removed mouse-tilt (overproduction per
  // user feedback). Entry spring stays. Visual restraint > visionOS pyrotechnics.

  return (
    <div style={{
      fontFamily: "'DM Sans', sans-serif",
      background: MATERIALS.gradientBackdrop(),
      color: COLORS.text,
      width: "100%", height: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
      position: "relative", overflow: "hidden",
    }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&family=Outfit:wght@300;400;600;700;800&display=swap" rel="stylesheet" />

      {/* Ambient orb decoration layer — sits behind everything (zIndex 0,
          pointerEvents none); orbs blur 80px and drift over 60s. The
          gradient backdrop above gives the orbs hue variation to pick up. */}
      <AmbientOrbs />

      {/* Theme toggle — thinGlass pill so it reads as a first-class
          surface element rather than a corner afterthought. */}
      <button
        onClick={toggleTheme}
        title={themeMode === "dark" ? "Switch to light mode" : "Switch to dark mode"}
        style={{
          position: "absolute", top: 16, right: 16, zIndex: 10,
          width: 40, height: 40,
          ...MATERIALS.thinGlass(),
          borderRadius: RADIUS.pill,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: COLORS.text, cursor: "pointer", fontSize: 18, padding: 0,
          boxShadow: MATERIALS.spatialShadow(),
          transition: "transform 0.2s",
        }}
      >
        {themeMode === "dark" ? "\u2600" : "\u263D"}
      </button>

      {/* Login Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 400, damping: 25 }}
        style={{
          position: "relative", zIndex: 1, width: "100%", maxWidth: 440,
          padding: "0 20px",
        }}
      >
        <style>{`
          ::placeholder { color: ${COLORS.textDim}; }
        `}</style>

        {/* Logo — the real I² logo (mark + MIRRORSME wordmark + tagline).
            Already carries the brand name, so no separate "I² Platform"
            text sits alongside it. Ink follows the active theme. */}
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          <Logo size={100} tone={themeMode === "dark" ? "white" : "dark"} style={{ margin: "0 auto 16px" }} />
          <div style={{ fontSize: 13, color: COLORS.textMuted, marginTop: 6 }}>
            {resetToken ? "Set a new password" : inviteToken ? "Set your password to get started" : mode === "forgot" ? "Reset your password" : mode === "login" ? "Sign in to your workspace" : "Create your workspace"}
          </div>
        </div>

        {/* Card — thinGlass + spatial shadow + subtle edge-shine + grain.
            Removed: cardGlow halo (was overproducing), mouse-tilt
            (visionOS pyrotechnics, distracting). Card opacity bumped
            to 16% in materials so text legibility holds without effects. */}
        <div
          style={{
            ...MATERIALS.thinGlass(),
            padding: "32px 28px",
            boxShadow: MATERIALS.spatialShadow(),
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Edge shine — 16px (was 32) for subtlety. */}
          <div style={{
            position: "absolute", top: 0, left: 0, right: 0, height: 16,
            background: MATERIALS.glassEdgeShine(),
            pointerEvents: "none",
            borderTopLeftRadius: "inherit",
            borderTopRightRadius: "inherit",
          }} />
          {/* Grain overlay — opacity 0.025 (was 0.04) for less noise on text. */}
          <div style={{
            position: "absolute", inset: 0, opacity: 0.025,
            backgroundImage: MATERIALS.grain,
            pointerEvents: "none", borderRadius: "inherit",
          }} />
          <div style={{ position: "relative", zIndex: 1 }}>
          {/* Error */}
          {error && (
            <div style={{
              padding: "10px 14px", borderRadius: 10, marginBottom: 20,
              background: `${COLORS.pink}10`, border: `1px solid ${COLORS.pink}25`,
              color: COLORS.pink, fontSize: 12, fontWeight: 500,
            }}>
              {error}
            </div>
          )}

          {/* Success message */}
          {success && (
            <div style={{
              padding: "10px 14px", borderRadius: 10, marginBottom: 20,
              background: `${COLORS.green}10`, border: `1px solid ${COLORS.green}25`,
              color: COLORS.green, fontSize: 12, fontWeight: 500,
            }}>
              {success}
            </div>
          )}

          {/* Reset Password Form (from email link) */}
          {resetToken ? (
            resetDone ? (
              <div style={{ textAlign: "center", padding: "16px 0" }}>
                <div style={{ fontSize: 28, marginBottom: 12 }}>✓</div>
                <div style={{ fontSize: 15, fontWeight: 600, color: COLORS.text, marginBottom: 8 }}>Password Reset Successfully</div>
                <div style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 20 }}>You can now sign in with your new password.</div>
                <button onClick={() => window.location.href = window.location.pathname} style={{
                  padding: "10px 24px", borderRadius: 10, border: "none", cursor: "pointer",
                  background: `linear-gradient(135deg, ${COLORS.accent}, #6366F1)`, color: "#fff",
                  fontSize: 13, fontWeight: 600, boxShadow: `0 4px 20px ${COLORS.accentGlow}`,
                }}>Back to Sign In</button>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div style={{
                  padding: "12px 14px", borderRadius: 10, marginBottom: 4,
                  background: `${COLORS.accent}10`, border: `1px solid ${COLORS.accent}25`,
                  color: COLORS.accentText, fontSize: 12, fontWeight: 500,
                }}>
                  Enter a new password for your account.
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 12, color: COLORS.textMuted, fontWeight: 500, marginBottom: 6 }}>New Password</label>
                  <Input type="password" placeholder="Min 8 characters" value={resetPassword}
                    onChange={(e) => setResetPassword(e.target.value)}
                    style={loginInputStyle} />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 12, color: COLORS.textMuted, fontWeight: 500, marginBottom: 6 }}>Confirm Password</label>
                  <Input type="password" placeholder="Re-enter password" value={resetConfirm}
                    onChange={(e) => setResetConfirm(e.target.value)}
                    style={loginInputStyle} />
                </div>
                <button onClick={handleResetPassword}
                  disabled={loading || !resetPassword || resetPassword.length < 8 || resetPassword !== resetConfirm}
                  style={{
                    width: "100%", padding: "13px 0", borderRadius: 10, border: "none",
                    background: (!resetPassword || resetPassword.length < 8 || resetPassword !== resetConfirm)
                      ? COLORS.border : `linear-gradient(135deg, ${COLORS.accent}, #6366F1)`,
                    color: (!resetPassword || resetPassword.length < 8 || resetPassword !== resetConfirm) ? COLORS.textDim : "#fff",
                    fontSize: 14, fontWeight: 700, cursor: loading ? "wait" : "pointer",
                    boxShadow: (!resetPassword || resetPassword.length < 8 || resetPassword !== resetConfirm)
                      ? "none" : `0 4px 20px ${COLORS.accentGlow}`,
                    transition: "all 0.3s", marginTop: 4, letterSpacing: "0.02em",
                  }}>
                  {loading ? "Resetting..." : "Reset Password"}
                </button>
              </div>
            )
          ) : inviteToken ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{
                padding: "12px 14px", borderRadius: 10, marginBottom: 4,
                background: `${COLORS.accent}10`, border: `1px solid ${COLORS.accent}25`,
                color: COLORS.accentText, fontSize: 12, fontWeight: 500,
              }}>
                You've been invited to join a workspace. Set a password to activate your account.
              </div>
              <div>
                <label style={{ display: "block", fontSize: 12, color: COLORS.textMuted, fontWeight: 500, marginBottom: 6 }}>Password</label>
                <Input type="password" placeholder="Min 8 characters" value={invitePassword}
                  onChange={(e) => setInvitePassword(e.target.value)}
                  style={loginInputStyle} />
              </div>
              <div>
                <label style={{ display: "block", fontSize: 12, color: COLORS.textMuted, fontWeight: 500, marginBottom: 6 }}>Confirm Password</label>
                <Input type="password" placeholder="Re-enter password" value={inviteConfirm}
                  onChange={(e) => setInviteConfirm(e.target.value)}
                  style={loginInputStyle} />
              </div>
              <button onClick={handleAcceptInvite}
                disabled={loading || !invitePassword || invitePassword.length < 8 || invitePassword !== inviteConfirm}
                style={{
                  width: "100%", padding: "13px 0", borderRadius: 10, border: "none",
                  background: (!invitePassword || invitePassword.length < 8 || invitePassword !== inviteConfirm)
                    ? COLORS.border : `linear-gradient(135deg, ${COLORS.green}, #059669)`,
                  color: (!invitePassword || invitePassword.length < 8 || invitePassword !== inviteConfirm) ? COLORS.textDim : "#fff",
                  fontSize: 14, fontWeight: 700, cursor: loading ? "wait" : "pointer",
                  boxShadow: (!invitePassword || invitePassword.length < 8 || invitePassword !== inviteConfirm)
                    ? "none" : `0 4px 20px rgba(16,185,129,0.3)`,
                  transition: "all 0.3s", marginTop: 4, letterSpacing: "0.02em",
                }}>
                {loading ? "Activating account..." : "Set Password & Join"}
              </button>
            </div>
          ) : (
          <>
          {/* Sign In only — self-registration disabled */}
          <div style={{ display: "flex", gap: 4, marginBottom: 28, background: COLORS.surface, borderRadius: 10, padding: 4 }}>
            {["login"].map((m) => (
              <button key={m} onClick={() => { setMode(m); setError(null); }} style={{
                flex: 1, padding: "9px 0", borderRadius: 8, border: "none", cursor: "pointer",
                background: mode === m ? COLORS.surface : "transparent",
                color: mode === m ? COLORS.text : COLORS.textMuted,
                fontSize: 13, fontWeight: 600, transition: "all 0.2s",
                boxShadow: mode === m ? "0 2px 8px rgba(0,0,0,0.2)" : "none",
              }}>{m === "login" ? "Sign In" : "Create Account"}</button>
            ))}
          </div>

          {/* Login Form */}
          {mode === "login" && (
            <form onSubmit={handleLogin} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div>
                <label style={{ display: "block", fontSize: 12, color: COLORS.textMuted, fontWeight: 500, marginBottom: 6 }}>Workspace ID</label>
                <Input type="text" placeholder="my-company" value={tenantSlug}
                  onChange={(e) => setTenantSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                  style={loginInputStyle} />
              </div>
              <div>
                <label style={{ display: "block", fontSize: 12, color: COLORS.textMuted, fontWeight: 500, marginBottom: 6 }}>Email</label>
                <Input type="email" placeholder="you@company.com" value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={loginInputStyle} />
              </div>
              <div>
                <label style={{ display: "block", fontSize: 12, color: COLORS.textMuted, fontWeight: 500, marginBottom: 6 }}>Password</label>
                <Input type="password" placeholder="••••••••" value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={loginInputStyle} />
              </div>
              {captcha.required && (
                <div>
                  <div style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 6 }}>
                    Quick check to confirm you're human:
                  </div>
                  <TurnstileWidget sitekey={captcha.sitekey} onToken={setCaptchaToken} onError={onCaptchaError} />
                  {captchaError && (
                    <div style={{ fontSize: 12, color: COLORS.pink, marginTop: 6, lineHeight: 1.4 }}>
                      Couldn't load the verification. Allow <code>challenges.cloudflare.com</code> and refresh — or wait a few minutes and sign in again.
                    </div>
                  )}
                </div>
              )}
              <motion.button
                type="submit"
                disabled={loading || !email || !password || !tenantSlug || (captcha.required && !captchaToken)}
                whileHover={(!email || !password || !tenantSlug) ? {} : { scale: 1.01 }}
                whileTap={(!email || !password || !tenantSlug) ? {} : { scale: 0.98 }}
                transition={{ type: "spring", stiffness: 400, damping: 25 }}
                style={{
                  width: "100%", padding: "13px 0", borderRadius: RADIUS.inner, border: "none",
                  background: (!email || !password || !tenantSlug) ? COLORS.border : `linear-gradient(135deg, ${COLORS.accent}, #6366F1)`,
                  color: (!email || !password || !tenantSlug) ? COLORS.textDim : "#FFFFFF",
                  fontSize: 14, fontWeight: 700, cursor: loading ? "wait" : "pointer",
                  boxShadow: (!email || !password || !tenantSlug) ? "none" : `0 4px 20px ${COLORS.accentGlow}`,
                  marginTop: 4, letterSpacing: "0.02em",
                }}
              >
                {loading ? "Signing in..." : "Sign In"}
              </motion.button>
              <div style={{ textAlign: "center", marginTop: 12 }}>
                <span onClick={() => { setMode("forgot"); setError(null); setSuccess(null); }}
                  style={{ fontSize: 12, color: COLORS.accentText, cursor: "pointer", fontWeight: 500 }}>
                  Forgot password?
                </span>
              </div>
            </form>
          )}

          {/* Forgot Password Form */}
          {mode === "forgot" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div>
                <label style={{ display: "block", fontSize: 12, color: COLORS.textMuted, fontWeight: 500, marginBottom: 6 }}>Workspace ID</label>
                <Input type="text" placeholder="my-company" value={forgotSlug}
                  onChange={(e) => setForgotSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                  style={loginInputStyle} />
              </div>
              <div>
                <label style={{ display: "block", fontSize: 12, color: COLORS.textMuted, fontWeight: 500, marginBottom: 6 }}>Email</label>
                <Input type="email" placeholder="you@company.com" value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  style={loginInputStyle} />
              </div>
              <button onClick={handleForgotPassword} disabled={loading || !forgotEmail || !forgotSlug} style={{
                width: "100%", padding: "13px 0", borderRadius: 10, border: "none",
                background: (!forgotEmail || !forgotSlug) ? COLORS.border : `linear-gradient(135deg, ${COLORS.accent}, #6366F1)`,
                color: (!forgotEmail || !forgotSlug) ? COLORS.textDim : "#fff",
                fontSize: 14, fontWeight: 700, cursor: loading ? "wait" : "pointer",
                boxShadow: (!forgotEmail || !forgotSlug) ? "none" : `0 4px 20px ${COLORS.accentGlow}`,
                transition: "all 0.3s", marginTop: 4, letterSpacing: "0.02em",
              }}>
                {loading ? "Sending..." : "Send Reset Link"}
              </button>
              <div style={{ textAlign: "center", marginTop: 4 }}>
                <span onClick={() => { setMode("login"); setError(null); setSuccess(null); }}
                  style={{ fontSize: 12, color: COLORS.accentText, cursor: "pointer", fontWeight: 500 }}>
                  Back to Sign In
                </span>
              </div>
            </div>
          )}

          {/* Register Form */}
          {mode === "register" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <label style={{ display: "block", fontSize: 12, color: COLORS.textMuted, fontWeight: 500, marginBottom: 6 }}>Organization Name</label>
                <Input type="text" placeholder="Acme Corp" value={regTenantName}
                  onChange={(e) => {
                    setRegTenantName(e.target.value);
                    setRegTenantSlug(e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-+$/, ""));
                  }}
                  style={loginInputStyle} />
              </div>
              <div>
                <label style={{ display: "block", fontSize: 12, color: COLORS.textMuted, fontWeight: 500, marginBottom: 6 }}>
                  Workspace ID
                  <span style={{ fontSize: 10, color: COLORS.textDim, marginLeft: 6, fontWeight: 400 }}>
                    (URL-safe, auto-generated)
                  </span>
                </label>
                <Input type="text" placeholder="acme-corp" value={regTenantSlug}
                  onChange={(e) => setRegTenantSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                  style={loginInputStyle} />
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: "block", fontSize: 12, color: COLORS.textMuted, fontWeight: 500, marginBottom: 6 }}>Your Name</label>
                  <Input type="text" placeholder="John Doe" value={regName}
                    onChange={(e) => setRegName(e.target.value)}
                    style={loginInputStyle} />
                </div>
              </div>
              <div>
                <label style={{ display: "block", fontSize: 12, color: COLORS.textMuted, fontWeight: 500, marginBottom: 6 }}>Email</label>
                <Input type="email" placeholder="john@acme.com" value={regEmail}
                  onChange={(e) => setRegEmail(e.target.value)}
                  style={loginInputStyle} />
              </div>
              <div>
                <label style={{ display: "block", fontSize: 12, color: COLORS.textMuted, fontWeight: 500, marginBottom: 6 }}>Password</label>
                <Input type="password" placeholder="Min 8 characters" value={regPassword}
                  onChange={(e) => setRegPassword(e.target.value)}
                  style={loginInputStyle} />
              </div>
              <button onClick={handleRegister}
                disabled={loading || !regEmail || !regPassword || !regTenantName || !regTenantSlug || !regName}
                style={{
                  width: "100%", padding: "13px 0", borderRadius: 10, border: "none",
                  background: (!regEmail || !regPassword || !regName || !regTenantSlug)
                    ? COLORS.border : `linear-gradient(135deg, ${COLORS.green}, #059669)`,
                  color: (!regEmail || !regPassword || !regName || !regTenantSlug) ? COLORS.textDim : "#fff",
                  fontSize: 14, fontWeight: 700, cursor: loading ? "wait" : "pointer",
                  boxShadow: (!regEmail || !regPassword || !regName || !regTenantSlug)
                    ? "none" : `0 4px 20px rgba(16,185,129,0.3)`,
                  transition: "all 0.3s", marginTop: 4, letterSpacing: "0.02em",
                }}>
                {loading ? "Creating workspace..." : "Create Workspace"}
              </button>
            </div>
          )}
          </>
          )}
          </div>
        </div>

        {/* Footer */}
        <div style={{ textAlign: "center", marginTop: 24, fontSize: 11, color: COLORS.textDim }}>
          I² Platform v1.0 — by Mirrorsme AI
        </div>
      </motion.div>
    </div>
  );
}
